import { PerformanceMonitor, Stats } from "@react-three/drei";
import { Canvas as CanvasComponent } from "@react-three/fiber";
import { Fragment, Suspense, useRef, useState } from "react";
import { Vector3 } from "three";
import { useConfig } from "../../context/ConfigContext";
import { useCabinetStore } from "../../context/StoreContext";
import { LayoutModus, useCanvasStore } from "../../hooks/useCanvasStore";
import { DragContextProvider } from "./context/DragProvider";
import { MaterialContextProvider } from "./context/MaterialProvider";
import { isLongPartsBuildMethod } from "./data";
import { useDebug } from "./debug";
import { Background } from "./parts/Background";
import { Compartment } from "./parts/Compartment";
import { CabinetDraggers } from "./parts/Compartment/CabinetDraggers";
import { Distributors } from "./parts/Compartment/Distributors";
import { Draggers } from "./parts/Compartment/Draggers";
import { Door } from "./parts/Door";
import { FEET_HEIGHT, Horizontal } from "./parts/Horizontal";
import { DrillingHoles, Shelf } from "./parts/Shelf";
import { Vertical } from "./parts/Vertical";
import Controls from "./parts/canvas/Controls";
import Dimensions from "./parts/canvas/Dimensions";
import { Figure } from "./parts/canvas/Figure";
import { Helpers } from "./parts/canvas/Helpers";
import Lights from "./parts/canvas/Lights";
import { Room } from "./parts/canvas/Room";
import { calculcateCameraZPosition } from "./utils";

export function Canvas() {
	const { state } = useCabinetStore();
	const { debugEnabled } = useDebug();
	const { mode } = useConfig();
	const [fullScreen, layoutMode] = useCanvasStore((store) => [store.fullScreen, store.layoutMode]);
	const ref = useRef<HTMLCanvasElement>(null);
	const [dpr, setDpr] = useState(2);

	const cameraFov = 25;
	const cameraAspect = Number(ref.current?.width) / Number(ref.current?.height);
	const figureDimensions = { width: 668 / 1000, height: 1805 / 1000 };
	const size = new Vector3(
		state.width / 1000,
		Math.max(state.height / 1000, figureDimensions.height),
		state.depth / 1000,
	);

	return (
		<CanvasComponent
			className="!touch-pan-y"
			ref={ref}
			gl={{ antialias: true }}
			shadows
			dpr={dpr}
			frameloop="demand"
			camera={{
				fov: cameraFov,
				position: [1, 1, calculcateCameraZPosition(cameraFov, cameraAspect, size)],
			}}
		>
			{debugEnabled && <Stats />}
			<Suspense fallback={null}>
				<MaterialContextProvider>
					<DragContextProvider>
						<group
							name="Stage"
							position={[
								-state.width / 1000 / 2,
								-state.height / 1000 / 2,
								-state.depth / 1000 / 2,
							]}
						>
							<group
								name="Cabinet"
								position={[0, isLongPartsBuildMethod(state.thickness) ? FEET_HEIGHT : 0, 0]}
							>
								<group name="Horizontals">
									{Array.from(state.horizontals.values(), (horizonal) => (
										<Horizontal key={horizonal.id} horizontal={horizonal} />
									))}
								</group>

								<group name="Verticals">
									{Array.from(state.verticals.values(), (vertical) => (
										<Vertical key={vertical.id} vertical={vertical} />
									))}
								</group>

								<group name="Compartments">
									{Array.from(state.compartments.values(), (compartment) => (
										<Fragment key={compartment.id}>
											{compartment.door && (
												<Door compartment={compartment} door={compartment.door} />
											)}

											{compartment.background && <Background background={compartment.background} />}

											{compartment.shelves.length > 0 && (
												<DrillingHoles compartment={compartment} />
											)}

											{compartment.shelves.map((shelf, index) => (
												<Shelf key={index} shelf={shelf} />
											))}
										</Fragment>
									))}
								</group>

								{Array.from(state.compartments.values(), (compartment) => (
									<Compartment key={compartment.id} compartment={compartment} />
								))}

								{mode !== "view" && mode !== "expandedView" && (
									<>
										{layoutMode === LayoutModus.move && (
											<Fragment>
												<Draggers />
												<CabinetDraggers />
											</Fragment>
										)}

										<Distributors />
									</>
								)}
							</group>

							<Room />
							<Figure />

							<Dimensions />
							<Lights />
							<Controls />
						</group>
					</DragContextProvider>
				</MaterialContextProvider>

				<Helpers />

				<PerformanceMonitor
					onChange={({ factor }) => {
						if (!fullScreen) {
							const newDpr = Math.round(0.5 + 1.5 * factor);

							return setDpr(newDpr);
						}
					}}
				/>
			</Suspense>
		</CanvasComponent>
	);
}
